import { z } from "zod";

/**
 * One Composio toolkit connection mirrored from Composio into Firestore.
 * Slug is the uppercase toolkit slug, e.g. "HUBSPOT"; status is Composio's
 * connection status ("ACTIVE" is the only state the nightly job acts on).
 */
export const IntegrationToolkit = z.object({
  slug: z.string().min(1),
  status: z.string().min(1),
  connectedAccountId: z.string().min(1),
});
export type IntegrationToolkit = z.infer<typeof IntegrationToolkit>;

/**
 * Per-adapter gather watermark, keyed by adapter key under `gatherCursors` on
 * the mirror doc. `lastOccurredAt` is the newest external event already turned
 * into insights (the next gather fetches strictly newer ones); it survives
 * empty gathers so a quiet week never re-widens the window.
 */
export const GatherCursor = z.object({
  lastOccurredAt: z.string().nullable().default(null),
  lastGatheredAt: z.string(),
});
export type GatherCursor = z.infer<typeof GatherCursor>;

/**
 * Mirror of a user's Composio connections, stored at
 * `Businesses/{businessId}/integrations/{uid}`. Written lazily from the apps
 * tRPC router whenever connection state is fetched, so the nightly insights
 * job can discover connected users without probing Composio. An empty
 * `toolkits` doc means "checked, nothing connected" — also useful state.
 * `gatherCursors` is written by the nightly job only; connection syncs must
 * never clobber it (the mirror sync writes with merge for this reason).
 */
export interface IntegrationsMirrorRecord {
  uid: string;
  toolkits: IntegrationToolkit[];
  gatherCursors: Record<string, GatherCursor>;
  updatedAt: string | null;
}

/**
 * Document id of the business connection mirror, a sibling of the per-user
 * mirror docs at `Businesses/{businessId}/integrations/__business`.
 *
 * Double-underscored so it cannot collide with a Firebase uid, and so every
 * query over the collection can recognise it by id alone. Readers that
 * enumerate *users* must skip it (see `listConnectedIntegrationUsers`).
 */
export const BUSINESS_INTEGRATIONS_DOC_ID = "__business";

/**
 * The business's connection to one toolkit.
 *
 * Composio has no notion of a connection owned by an organisation: every
 * connected account belongs to a user id. So a business connection is an
 * ordinary private connection held by the admin who made it, and *this* record
 * is what makes it the company's — `ownerUid` is whose Composio identity the
 * session runs as, not who is allowed to use it. Every member reads through it.
 *
 * `connectedAccountId` pins the session to this exact account, so an admin who
 * later connects a second account for the same toolkit cannot silently change
 * which one the nightly jobs read.
 */
export const BusinessIntegrationToolkit = z.object({
  /** Uppercase toolkit slug, e.g. "CUSTOM_UPSALES_MCP". */
  slug: z.string().min(1),
  connectedAccountId: z.string().min(1),
  /** The Graftus uid whose Composio identity holds the connection. */
  ownerUid: z.string().min(1),
  connectedAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
});
export type BusinessIntegrationToolkit = z.infer<
  typeof BusinessIntegrationToolkit
>;

/**
 * Mirror of the business's own Composio connections, stored at
 * `Businesses/{businessId}/integrations/__business`. One entry per toolkit at
 * most — several workspace connections for one toolkit is deliberately out of
 * scope, so promoting a connection replaces whatever was there.
 */
export interface BusinessIntegrationsRecord {
  toolkits: BusinessIntegrationToolkit[];
  /** Admin overrides of which CRM answers for each kind of data (GRA-116). */
  dataSources: BusinessDataSources;
  updatedAt: string | null;
}

// ---------------------------------------------------------------------------
// Per-business data sources (GRA-116)
//
// A business mixes sales systems: Upsales for meetings and deals with Garba
// for phone calls, say. Which system answers for which kind of data is
// resolved from the connected CRMs and their capabilities, with an optional
// admin override per kind. The override is advisory, never authoritative: it
// is validated against the connected CRMs on every read, so a stale or
// impossible entry falls back to the default and nothing needs migrating.
// ---------------------------------------------------------------------------

/** The kinds of CRM data a source is chosen for. Revenue and pipeline are deals. */
export const CRM_DATA_KINDS = ["deal", "meeting", "call", "task"] as const;
export const CrmDataKind = z.enum(CRM_DATA_KINDS);
export type CrmDataKind = z.infer<typeof CrmDataKind>;

/**
 * Admin overrides, kind -> canonical CRM toolkit slug, stored as the
 * `dataSources` field of `Businesses/{businessId}/integrations/__business`.
 * Advisory; validated against the connected CRMs on every read.
 */
export const BusinessDataSources = z.record(CrmDataKind, z.string().min(1));
export type BusinessDataSources = z.infer<typeof BusinessDataSources>;
